import { pluralizeWord } from '@doveit/hammer';
import { Property, PropertyPreview } from '../../../providers/api/dtos';
import { ScroogeProperty } from '../../../providers/api/dtos/scrooge';
import { imageUrlToBase64 } from '../../../utils/file/file';
import type {
  BrandTemplateAutofillPropertyData, BrandTemplateAutofillResolvedField, BrandTemplateAutofillTransformerContext, BrandTemplateAutofillTransformerRegistry,
} from './types';
import { BrandTemplateAutofillParams, MarketingOpportunityTrigger } from '../../types';
import { raise } from '../../../utils';
import { BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM, BRAND_TEMPLATE_AUTOFILL_PARAMS, BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM } from '../../constants';

export const createTransformerRegistry = <T extends BrandTemplateAutofillTransformerRegistry>(registry: T): T => registry;

/**
 * Creates a transformer that resolves a text autofill field from an entity source.
 *
 * @param get - Extracts a string value from the entity source and transformer context.
 * @returns A transformer returning a `text` autofill field, or `undefined` if the value is empty.
 *
 * @example
 * const transformer = textField((agent) => agent?.name);
 */
export const textField = <T>(get: (source: T | undefined, context: BrandTemplateAutofillTransformerContext) => string | undefined | null) => (source: T | undefined, context: BrandTemplateAutofillTransformerContext): BrandTemplateAutofillResolvedField | undefined => {
  const value = get(source, context);
  return value ? { type: 'text', text: value } : undefined;
};

/**
 * Creates a transformer that resolves an image autofill field from an entity source,
 * producing base64-encoded image data via the given fetcher.
 *
 * @param get - Extracts the value (e.g. a URL, or data to encode) from the entity source and transformer context.
 * @param base64Generator - Resolves the extracted value into base64 image data and a filename. Defaults to fetching the value as an image URL.
 * @returns A transformer returning an `image` autofill field, or `undefined` if the value is empty or the fetcher fails.
 *
 * @example
 * const transformer = imageField((property, { positions: [i] }) => property?.photos?.get(i)?.url);
 *
 * @example
 * // with a custom base64 generator, e.g. generating a QR code instead of fetching a URL
 * const transformer = imageField((property) => `https://example.com/listings/${property?.id}`, qrValueToBase64);
 */
export const imageField = <T>(
  get: (source: T | undefined, context: BrandTemplateAutofillTransformerContext) => string | undefined | null,
  base64Generator: (value: string) => Promise<{ base64: string, name: string }> = imageUrlToBase64,
) => async (source: T | undefined, context: BrandTemplateAutofillTransformerContext): Promise<BrandTemplateAutofillResolvedField | undefined> => {
    const value = get(source, context);
    if (!value) return undefined;

    try {
      const { base64, name } = await base64Generator(value);
      return { type: 'image', base64, name };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`failed to resolve image field for value "${value}":`, err);
      return undefined;
    }
  };

export function getPropertySoldTimeUnit(property?: BrandTemplateAutofillPropertyData) {
  if (!property?.soldData) return undefined;

  return property.soldData.unit === 'DAYS'
    ? pluralizeWord('giorn', 'o', 'i', property.soldData.time === 0 ? 1 : property.soldData.time)
    : pluralizeWord('settiman', 'a', 'e', property.soldData.time);
}

export function getPropertySoldTimeUnitLabel(property?: BrandTemplateAutofillPropertyData) {
  if (!property?.soldData) return undefined;

  if (property.soldData.unit === 'DAYS') {
    const normalizedSalesTimeDays = property.soldData.time === 0 ? 1 : property.soldData.time;

    return `${normalizedSalesTimeDays} ${pluralizeWord('giorn', 'o', 'i', normalizedSalesTimeDays)}`;
  }

  return `${property.soldData.time} ${pluralizeWord('settiman', 'a', 'e', property.soldData.time)}`;
}

export const toBrandTemplateAutofillPropertyData = (
  property?: Property | PropertyPreview,
  scroogeProperty?: ScroogeProperty,
): BrandTemplateAutofillPropertyData => ({
  ...(property ?? {}),
  ...(scroogeProperty && {
    publicationAddress: scroogeProperty.normalizedAddress,
    publicationAddressShort: scroogeProperty.route,
    moneySaved: scroogeProperty.moneySaved,
    soldData: scroogeProperty.soldData,
    roomsNumber: scroogeProperty.roomsNumber,
  }),
  realAddressShort: (property?.geo?.route && property?.geo?.streetNumber) ? `${property.geo.route} ${property.geo.streetNumber}` : property?.geo?.route,
});

/**
 * Builds a stable identity string for a marketing trigger, used to deduplicate
 * registrations and as a React key. Must cover every field that affects behaviour.
 *
 * @example
 * getMarketingTriggerKey({ type: 'property-sold', propertyId: 42 }); // 'property-sold:42'
 */
export const getMarketingTriggerKey = (
  trigger: MarketingOpportunityTrigger,
): string => {
  switch (trigger.type) {
    case 'property-sold':
      return `property-sold:${trigger.propertyId}${trigger.agentId != null ? `:agent:${trigger.agentId}` : ''}`;

    case 'property-live':
      return `property-live:${trigger.propertyId}${trigger.agentId != null ? `:agent:${trigger.agentId}` : ''}`;

    case 'property-recently-published':
      return `property-recently-published:${trigger.propertyId}${trigger.agentId != null ? `:agent:${trigger.agentId}` : ''}`;

    default:
      return raise(`unknown marketing trigger: ${JSON.stringify(trigger)}`);
  }
};

/**
 * Builds the path to the Canva brand template fill page, with autofill params in
 * the query string. Params are emitted in constant order; `undefined` and empty
 * values are skipped, `0` is kept.
 *
 * @example
 * buildCanvaFillPath('TPL-1', { propertyId: 42 }); // '.../TPL-1/fill?propertyId=42'
 */
export const buildCanvaFillPath = (
  brandTemplateId: string,
  params?: BrandTemplateAutofillParams,
): string => {
  const path = `/marketing/canva/brand-templates/${brandTemplateId}/fill`;

  if (!params) {
    return path;
  }

  const searchParams = new URLSearchParams();

  BRAND_TEMPLATE_AUTOFILL_PARAMS.forEach((param) => {
    const value = params[param];

    if (value !== undefined && value !== '') {
      searchParams.set(param, String(value));
    }
  });

  const queryString = searchParams.toString();

  return queryString ? `${path}?${queryString}` : path;
};

/**
 * Maps a marketing trigger to its autofill params. Returns `{}` for triggers with
 * no autofill data, so the path degrades to the bare fill page.
 *
 * @example
 * getTriggerAutofillParams({ type: 'property-sold', propertyId: 42 }); // { propertyId: 42 }
 */
export const getTriggerAutofillParams = (
  trigger?: MarketingOpportunityTrigger,
): BrandTemplateAutofillParams => {
  if (!trigger) {
    return {};
  }

  switch (trigger.type) {
    case 'property-sold':
    case 'property-live':
    case 'property-recently-published':
      return {
        [BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM]: trigger.propertyId,
        [BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM]: trigger.agentId,
      };
    default:
      return {};
  }
};
